// pages/EmployeeOvertime.jsx
// Dashboard Employee Overtime — read dari tabel employee_overtime via /api/overtime/*
import { useState, useEffect, useCallback } from 'react';
import { api as axiosApi } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import useTabDeepLink from '../hooks/useTabDeepLink';

// Tab yang boleh datang dari `?tab=` (QuickSearch).
const OT_TABS = ['overview', 'detail', 'pivot'];

// ── useApi — pakai axios instance dari AuthContext (token otomatis via interceptor) ──
// override (opsional): fungsi (path) => Promise<json> — dipakai mode share (cookie,
// tanpa JWT). Default TIDAK berubah — perilaku login asli tetap sama persis.
function useApi(override) {
  return useCallback(async (path, opts = {}) => {
    if (override) return override(path, opts);
    const method = (opts.method || 'GET').toLowerCase();
    const body   = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = method === 'get' || method === 'delete'
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message || 'API error');
    }
  }, [override]);
}

// ── Utilities ──────────────────────────────────────────────────────────────────

function fmt(n)    { return Number(n || 0).toLocaleString('id-ID'); }
function fmtMoney(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID'); }
function fmtHours(n) { return parseFloat(n || 0).toFixed(1) + ' h'; }
function shortVal(v) {
  if (v === null || v === undefined) return '—';
  const s = String(v);
  return s.length > 40 ? s.slice(0, 38) + '…' : s;
}

// ── Shared UI ──────────────────────────────────────────────────────────────────

function Card({ children, style = {} }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: '1rem 1.25rem', ...style }}>
      {children}
    </div>
  );
}

function StatCard({ label, value, sub, color = 'rgb(var(--brand-600))', icon }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1rem 1.25rem', minWidth: 150, flex: '1 1 150px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Badge({ text, color = '#64748b' }) {
  return (
    <span style={{ display: 'inline-block', background: color + '18', color, border: `1px solid ${color}40`, borderRadius: 6, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
      {text}
    </span>
  );
}

function Alert({ msg, type = 'info', onClose }) {
  if (!msg) return null;
  const c = {
    success: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
    error:   { bg: '#fee2e2', color: '#7f1d1d', border: '#fca5a5' },
    info:    { bg: 'rgb(var(--brand-100))', color: '#1e3a5f', border: '#93c5fd' },
  }[type] || {};
  return (
    <div style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{msg}</span>
      {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.color, fontSize: 18, lineHeight: 1, marginLeft: 8 }}>×</button>}
    </div>
  );
}

function Loading() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', color: '#94a3b8', fontSize: 14 }}>
      <span style={{ marginRight: 8 }}>⏳</span> Memuat data…
    </div>
  );
}

function Empty({ text }) {
  return (
    <tr>
      <td colSpan={99} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8', fontSize: 14 }}>
        {text}
      </td>
    </tr>
  );
}

const inputStyle = {
  padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8,
  fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff',
  boxSizing: 'border-box',
};

const Th = ({ children, right }) => (
  <th style={{ background: '#f8fafc', padding: '9px 12px', textAlign: right ? 'right' : 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
    {children}
  </th>
);
const Td = ({ children, right, title }) => (
  <td title={title} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#0f172a', whiteSpace: 'nowrap', textAlign: right ? 'right' : 'left' }}>
    {children}
  </td>
);

// ── Summary Bar Chart (pure CSS) ───────────────────────────────────────────────

function BarChart({ data, valueKey, labelKey, color = '#f59e0b', formatVal = (v) => fmt(v), noDataText = 'No data.' }) {
  if (!data || data.length === 0) return <div style={{ color: '#94a3b8', fontSize: 13, padding: '1rem 0' }}>{noDataText}</div>;
  const max = Math.max(...data.map(d => d[valueKey] || 0)) || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {data.map((d, i) => {
        const pct = Math.max(2, ((d[valueKey] || 0) / max) * 100);
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 110, fontSize: 12, color: '#475569', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d[labelKey]}>
              {d[labelKey]}
            </div>
            <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 18, overflow: 'hidden' }}>
              <div style={{ width: pct + '%', background: color, height: '100%', borderRadius: 4, transition: 'width 0.4s ease', display: 'flex', alignItems: 'center', paddingLeft: 6 }}>
                <span style={{ fontSize: 10, color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {formatVal(d[valueKey])}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const PAGE = 100;

export default function EmployeeOvertime({ dataFetcher } = {}) {
  const api = useApi(dataFetcher);
  const { lang, t } = useLang();
  const id = lang !== 'en';
  const MONTHS = id
    ? ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agt','Sep','Okt','Nov','Des']
    : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const T = {
    subtitle:      (recs, files) => t('overtime.subtitle', { recs, files }),
    tabOverview:   t('overtime.tabOverview'),
    tabDetail:     t('overtime.tabDetail'),
    tabPivot:      t('overtime.tabPivot'),
    pivotTitle:    t('overtime.pivotTitle'),
    measure:       t('overtime.measure'),
    measureHours:  t('overtime.measureHours'),
    measureAmount: t('overtime.measureAmount'),
    measureCount:  t('overtime.measureCount'),
    rowTotal:      t('overtime.rowTotal'),
    colTotal:      t('overtime.colTotal'),
    grandTotal:    t('overtime.grandTotal'),
    expandAll:     t('overtime.expandAll'),
    collapseAll:   t('overtime.collapseAll'),
    clickSort:     t('overtime.clickSort'),
    clickReason:   t('overtime.clickReason'),
    reasonBreakdown: t('overtime.reasonBreakdown'),
    value:         t('overtime.value'),
    allPeriods:    t('overtime.allPeriods'),
    filter:        t('overtime.filter'),
    allYears:      t('overtime.allYears'),
    allMonths:     t('overtime.allMonths'),
    allBu:         t('overtime.allBu'),
    searchPh:      t('overtime.searchPh'),
    apply:         t('overtime.apply'),
    reset:         t('overtime.reset'),
    totalRecords:  t('overtime.totalRecords'),
    businessUnits: t('overtime.businessUnits'),
    periodsAvail:  t('overtime.periodsAvail'),
    sourceFiles:   t('overtime.sourceFiles'),
    page:          t('overtime.page'),
    totalHoursPer: t('overtime.totalHoursPer'),
    totalAmountPer:(by) => t('overtime.totalAmountPer', { by }),
    optDept:       t('overtime.optDept'),
    optSection:    t('overtime.optSection'),
    optBu:         t('overtime.optBu'),
    noData:        t('overtime.noData'),
    detailTitle:   t('overtime.detailTitle'),
    records:       (n) => t('overtime.records', { n }),
    pageShort:     (p, tp) => t('overtime.pageShort', { p, tp }),
    loadFail:      t('overtime.loadFail'),
    emptyTable:    t('overtime.emptyTable'),
    colKode:       t('overtime.colKode'),
    colNama:       t('overtime.colNama'),
    colThn:        t('overtime.colThn'),
    colBln:        t('overtime.colBln'),
    colTgl:        t('overtime.colTgl'),
    colMulai:      t('overtime.colMulai'),
    colSelesai:    t('overtime.colSelesai'),
    colJam:        t('overtime.colJam'),
    colTotalJam:   t('overtime.colTotalJam'),
    colAmount:     t('overtime.colAmount'),
    colReason:     t('overtime.colReason'),
  };
  const byLabel = (k) => k === 'business_unit' ? T.optBu : k === 'department' ? T.optDept : T.optSection;
  const [stat, setStat]           = useState({});
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [msg, setMsg]             = useState(null);
  const [page, setPage]           = useState(0);
  const [search, setSearch]       = useState('');
  const [filterYear,  setFilterYear]  = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterBu,    setFilterBu]    = useState('');
  const [summaryBy,   setSummaryBy]   = useState('department'); // department | section | business_unit
  const [summary,     setSummary]     = useState([]);
  const [searchParams] = useSearchParams();
  const [activeTab,   setActiveTab]   = useState(() => {
    const p = searchParams.get('tab');
    return OT_TABS.includes(p) ? p : 'overview';
  }); // overview | detail | pivot
  useTabDeepLink('employee_overtime', (p) => OT_TABS.includes(p), setActiveTab);
  const [sortBy,      setSortBy]      = useState('');         // '' = default order
  const [sortDir,     setSortDir]     = useState('desc');
  const [pivot,       setPivot]       = useState(null);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [pivotMeasure, setPivotMeasure] = useState('amount'); // hours | amount | count
  const [pvExpanded, setPvExpanded] = useState(() => new Set()); // node keys yang terbuka
  const [pvSortCol, setPvSortCol]   = useState(-1); // -1 = no sort; index kolom; 'total'
  const [pvSortDir, setPvSortDir]   = useState('desc');
  const [drill, setDrill]           = useState(null); // konteks sel yang diklik
  const [drillData, setDrillData]   = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  // ── Build query string ──
  const buildQs = (p, q, y, m, bu, sb = sortBy, sd = sortDir) => {
    const params = [`limit=${PAGE}`, `offset=${p * PAGE}`];
    if (q)  params.push(`search=${encodeURIComponent(q)}`);
    if (y)  params.push(`year=${y}`);
    if (m)  params.push(`month=${m}`);
    if (bu) params.push(`bu=${encodeURIComponent(bu)}`);
    if (sb) { params.push(`sort_by=${sb}`); params.push(`sort_dir=${sd}`); }
    return '/api/overtime/data?' + params.join('&');
  };

  // ── Load status + table data ──
  const load = useCallback(async (p = 0, q = '', y = '', m = '', bu = '', sb = '', sd = 'desc') => {
    setLoading(true);
    try {
      const qsParams = [`limit=${PAGE}`, `offset=${p * PAGE}`];
      if (q)  qsParams.push(`search=${encodeURIComponent(q)}`);
      if (y)  qsParams.push(`year=${y}`);
      if (m)  qsParams.push(`month=${m}`);
      if (bu) qsParams.push(`bu=${encodeURIComponent(bu)}`);
      if (sb) { qsParams.push(`sort_by=${sb}`); qsParams.push(`sort_dir=${sd}`); }
      const [s, d] = await Promise.all([
        api('/api/overtime/status'),
        api('/api/overtime/data?' + qsParams.join('&')),
      ]);
      setStat(s || {});
      setRows(d.data || []);
      setTotal(d.total || 0);
      setPage(p);
    } catch (e) {
      setMsg({ text: e.message || T.loadFail, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [api]);

  // ── Load summary (aggregasi per group) ──
  const loadSummary = useCallback(async (groupBy, y = '', m = '', bu = '') => {
    setSummaryLoading(true);
    try {
      const params = [`group_by=${groupBy}`];
      if (y)  params.push(`year=${y}`);
      if (m)  params.push(`month=${m}`);
      if (bu) params.push(`bu=${encodeURIComponent(bu)}`);
      const res = await api('/api/overtime/summary?' + params.join('&'));
      setSummary(res.data || []);
    } catch (_) {
      setSummary([]);
    } finally {
      setSummaryLoading(false);
    }
  }, [api]);

  // ── Initial load ──
  useEffect(() => {
    load(0);
    loadSummary('department');
  }, [load, loadSummary]);

  // ── Apply filters ──
  const applyFilters = (p = 0, y = filterYear, m = filterMonth, bu = filterBu, q = search) => {
    load(p, q, y, m, bu, sortBy, sortDir);
    loadSummary(summaryBy, y, m, bu);
    if (activeTab === 'pivot') loadPivot(pivotMeasure, y, m, bu);
  };

  const handleReset = () => {
    setFilterYear(''); setFilterMonth(''); setFilterBu(''); setSearch('');
    setSortBy(''); setSortDir('desc');
    load(0, '', '', '', '', '', 'desc');
    loadSummary(summaryBy, '', '', '');
  };

  // ── Sort handler (server-side, urut seluruh dataset) ──
  const handleSort = (col) => {
    const nextDir = (sortBy === col && sortDir === 'desc') ? 'asc' : 'desc';
    setSortBy(col); setSortDir(nextDir);
    load(0, search, filterYear, filterMonth, filterBu, col, nextDir);
  };

  const handleSummaryBy = (val) => {
    setSummaryBy(val);
    loadSummary(val, filterYear, filterMonth, filterBu);
  };

  // ── Load pivot (BU × Department) ──
  const loadPivot = useCallback(async (measure, y = '', m = '', bu = '') => {
    setPivotLoading(true);
    try {
      const params = [`measure=${measure}`];
      if (y)  params.push(`year=${y}`);
      if (m)  params.push(`month=${m}`);
      if (bu) params.push(`bu=${encodeURIComponent(bu)}`);
      const res = await api('/api/overtime/pivot?' + params.join('&'));
      setPivot(res);
      // default: collapse all
      setPvExpanded(new Set());
    } catch (e) {
      setMsg({ text: e.message || T.loadFail, type: 'error' });
      setPivot(null);
    } finally {
      setPivotLoading(false);
    }
  }, [api]);

  // ── Drilldown: breakdown per reason saat sel pivot diklik ──
  const loadDrill = useCallback(async (ctx) => {
    setDrill(ctx); setDrillData(null); setDrillLoading(true);
    try {
      const params = [`measure=${ctx.measure}`];
      if (ctx.bu)   params.push(`bu=${encodeURIComponent(ctx.bu)}`);
      if (ctx.dept) params.push(`dept=${encodeURIComponent(ctx.dept)}`);
      if (ctx.sec)  params.push(`sec=${encodeURIComponent(ctx.sec)}`);
      if (ctx.year) params.push(`year=${ctx.year}`);
      if (ctx.month) params.push(`month=${ctx.month}`);
      const res = await api('/api/overtime/reason-breakdown?' + params.join('&'));
      setDrillData(res);
    } catch (e) {
      setMsg({ text: e.message || T.loadFail, type: 'error' });
      setDrill(null);
    } finally {
      setDrillLoading(false);
    }
  }, [api]);

  // Muat pivot saat tab pivot dibuka / measure / filter berubah
  useEffect(() => {
    if (activeTab === 'pivot') loadPivot(pivotMeasure, filterYear, filterMonth, filterBu);
  }, [activeTab, pivotMeasure, loadPivot]); // eslint-disable-line

  const periods  = stat.periods       || [];
  const bus      = stat.business_units || [];
  const sources  = stat.sources       || [];
  const totalPages = Math.ceil(total / PAGE);

  // Agregasi ringkasan dari stat untuk top stats
  const totalAmount = sources.reduce((acc, _) => acc, 0); // hitung dari rows nanti
  const periodLabel = filterYear && filterMonth
    ? `${MONTHS[parseInt(filterMonth) - 1]} ${filterYear}`
    : filterYear || T.allPeriods;

  const COLS = [
    { key: 'employee_code', label: T.colKode },
    { key: 'employee_name', label: T.colNama },
    { key: 'business_unit', label: 'BU' },
    { key: 'department',    label: T.optDept },
    { key: 'section',       label: T.optSection },
    { key: 'year',          label: T.colThn },
    { key: 'month',         label: T.colBln },
    { key: 'duty_date',     label: T.colTgl },
    { key: 'ot_start',      label: T.colMulai },
    { key: 'ot_end',        label: T.colSelesai },
    { key: 'ot_hours',      label: T.colJam,      right: true, fmt: fmtHours },
    { key: 'total_ot_hours',label: T.colTotalJam,  right: true, fmt: fmtHours },
    { key: 'ot_amount',     label: T.colAmount,    right: true, fmt: fmtMoney },
    { key: 'reason',        label: T.colReason },
  ];

  return (
    <div style={{ padding: '1.5rem', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", maxWidth: 1600, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 24 }}>⏱️</span>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>Employee Overtime</h2>
          <Badge text={periodLabel} color="#f59e0b" />
        </div>
        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
          {T.subtitle(fmt(stat.count || 0), sources.length)}
        </p>
      </div>

      <Alert msg={msg?.text} type={msg?.type} onClose={() => setMsg(null)} />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1rem', borderBottom: '2px solid #e2e8f0' }}>
        {[
          { key: 'overview', label: '📊 ' + T.tabOverview },
          { key: 'detail',   label: '📋 ' + T.tabDetail },
          { key: 'pivot',    label: '📐 ' + T.tabPivot },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              background: 'none', border: 'none', fontFamily: 'inherit',
              color: activeTab === tab.key ? 'rgb(var(--brand-600))' : '#64748b',
              borderBottom: activeTab === tab.key ? '2px solid rgb(var(--brand-600))' : '2px solid transparent',
              marginBottom: -2,
            }}>
            {tab.label}
          </button>
        ))}
      </div>


      {/* Filter Bar */}
      <Card style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginRight: 4 }}>🔽 {T.filter}</span>
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)} style={{ ...inputStyle, width: 100 }}>
            <option value="">{T.allYears}</option>
            {[...new Set(periods.map(p => p.year))].sort((a,b) => b - a).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ ...inputStyle, width: 100 }}>
            <option value="">{T.allMonths}</option>
            {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={filterBu} onChange={e => setFilterBu(e.target.value)} style={{ ...inputStyle, width: 130 }}>
            <option value="">{T.allBu}</option>
            {bus.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && applyFilters()}
            placeholder={T.searchPh}
            style={{ ...inputStyle, width: 200 }}
          />
          <button onClick={() => applyFilters()} style={{ ...inputStyle, width: 'auto', background: 'rgb(var(--brand-600))', color: '#fff', fontWeight: 600, cursor: 'pointer', border: 'none', padding: '8px 16px' }}>
            {T.apply}
          </button>
          <button onClick={handleReset} style={{ ...inputStyle, width: 'auto', background: '#f1f5f9', color: '#475569', fontWeight: 600, cursor: 'pointer', border: '1px solid #e2e8f0', padding: '8px 14px' }}>
            {T.reset}
          </button>
        </div>
      </Card>

      {/* ── TAB: OVERVIEW ── */}
      {activeTab === 'overview' && (<>
      {/* Stat Cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <StatCard icon="📋" label={T.totalRecords}   value={fmt(total)}           color="rgb(var(--brand-600))" />
        <StatCard icon="🏢" label={T.businessUnits}  value={bus.length}           color="#f59e0b" />
        <StatCard icon="📅" label={T.periodsAvail} value={periods.length}      color="#10b981" sub={periods.length ? `${periods[periods.length-1]?.year}/${String(periods[periods.length-1]?.month).padStart(2,'0')} – ${periods[0]?.year}/${String(periods[0]?.month).padStart(2,'0')}` : ''} />
        <StatCard icon="🗂️" label={T.sourceFiles}    value={sources.length}       color="#8b5cf6" />
        <StatCard icon="📄" label={T.page}         value={`${page+1} / ${totalPages || 1}`} color="#64748b" />
      </div>

      {/* Summary + Chart */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>

        {/* Chart — Total Jam OT */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{T.totalHoursPer}</span>
            <select value={summaryBy} onChange={e => handleSummaryBy(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              <option value="department">{T.optDept}</option>
              <option value="section">{T.optSection}</option>
              <option value="business_unit">{T.optBu}</option>
            </select>
          </div>
          {summaryLoading ? <Loading /> : (
            <BarChart
              data={summary.slice(0, 10)}
              valueKey="total_jam"
              labelKey="group_name"
              color="#f59e0b"
              formatVal={v => parseFloat(v || 0).toFixed(0) + 'h'}
              noDataText={T.noData}
            />
          )}
        </Card>

        {/* Chart — Total Amount */}
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 12 }}>
            {T.totalAmountPer(byLabel(summaryBy))}
          </div>
          {summaryLoading ? <Loading /> : (
            <BarChart
              data={summary.slice(0, 10)}
              valueKey="total_amount"
              labelKey="group_name"
              color="#10b981"
              formatVal={v => 'Rp ' + fmt(v)}
              noDataText={T.noData}
            />
          )}
        </Card>
      </div>
      </>)}

      {/* ── TAB: OVERTIME DETAIL ── */}
      {activeTab === 'detail' && (
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{T.detailTitle}</span>
            <Badge text={T.records(fmt(total))} color="#f59e0b" />
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button onClick={() => applyFilters(page - 1)} disabled={page === 0}
                style={{ ...inputStyle, width: 'auto', padding: '5px 12px', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}>
                ◀
              </button>
              <span style={{ fontSize: 12, color: '#64748b' }}>{T.pageShort(page+1, totalPages)}</span>
              <button onClick={() => applyFilters(page + 1)} disabled={page >= totalPages - 1}
                style={{ ...inputStyle, width: 'auto', padding: '5px 12px', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>
                ▶
              </button>
            </div>
          )}
        </div>

        {loading ? <Loading /> : (
          <div style={{ overflowX: 'auto', maxHeight: 520, overflowY: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 'max-content' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <tr>{COLS.map(c => {
                  const active = sortBy === c.key;
                  const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
                  return (
                    <th key={c.key}
                      onClick={() => handleSort(c.key)}
                      title="Klik untuk urut"
                      style={{ background: active ? 'rgb(var(--brand-50))' : '#f8fafc', padding: '9px 12px',
                        textAlign: c.right ? 'right' : 'left', fontWeight: 600, fontSize: 11,
                        color: active ? 'rgb(var(--brand-600))' : '#64748b', borderBottom: '2px solid #e2e8f0',
                        whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em',
                        cursor: 'pointer', userSelect: 'none' }}>
                      {c.label}{arrow}
                    </th>
                  );
                })}</tr>
              </thead>
              <tbody>
                {rows.length === 0 && <Empty text={T.emptyTable} />}
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {COLS.map(c => {
                      const val = row[c.key];
                      if (c.key === 'month') return <Td key={c.key}><Badge text={MONTHS[(val || 1) - 1] || val} color="#64748b" /></Td>;
                      if (c.key === 'business_unit') return <Td key={c.key}><Badge text={val || '—'} color="#f59e0b" /></Td>;
                      if (c.fmt) return <Td key={c.key} right={c.right}><strong>{c.fmt(val)}</strong></Td>;
                      return <Td key={c.key} right={c.right} title={String(val || '')}>{shortVal(val)}</Td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}

      {/* ── TAB: PIVOT ── */}
      {activeTab === 'pivot' && (
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{T.pivotTitle}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => {
              // expand semua: kumpulkan semua key bu + bu>dept
              const all = new Set();
              (pivot?.rows || []).forEach(bu => {
                all.add(bu.label);
                (bu.children || []).forEach(d => all.add(bu.label + '>' + d.label));
              });
              setPvExpanded(all);
            }} style={{ ...inputStyle, width: 'auto', cursor: 'pointer', padding: '6px 10px' }}>⊞ {T.expandAll}</button>
            <button onClick={() => setPvExpanded(new Set())} style={{ ...inputStyle, width: 'auto', cursor: 'pointer', padding: '6px 10px' }}>⊟ {T.collapseAll}</button>
            <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>{T.measure}</span>
            <select value={pivotMeasure} onChange={e => setPivotMeasure(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              <option value="hours">{T.measureHours}</option>
              <option value="amount">{T.measureAmount}</option>
              <option value="count">{T.measureCount}</option>
            </select>
          </div>
        </div>

        {pivotLoading ? <Loading /> : !pivot || pivot.rows.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>{T.emptyTable}</div>
        ) : (() => {
          const fmtCell = (v) => v <= 0 ? '—' : pivotMeasure === 'amount'
            ? 'Rp ' + fmt(v)
            : pivotMeasure === 'count' ? fmt(v) : parseFloat(v || 0).toFixed(1);
          const colLabel = (c) => `${MONTHS[(c.month || 1) - 1] || c.month} ${c.year}`;
          const toggle = (key) => setPvExpanded(prev => {
            const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
          });

          // ── Sort tree rekursif berdasar kolom terpilih (pvSortCol) ──
          const cellVal = (node) => pvSortCol === 'total' ? node.rowTotal
            : (pvSortCol >= 0 ? (node.cells[pvSortCol] || 0) : 0);
          const sortNodes = (arr) => {
            if (pvSortCol === -1) return arr;
            const sorted = [...arr].sort((a, b) =>
              pvSortDir === 'asc' ? cellVal(a) - cellVal(b) : cellVal(b) - cellVal(a));
            return sorted.map(n => n.children ? { ...n, children: sortNodes(n.children) } : n);
          };
          const sortedRows = sortNodes(pivot.rows);

          const sortCol = (idx) => {
            if (pvSortCol === idx) setPvSortDir(d => d === 'desc' ? 'asc' : 'desc');
            else { setPvSortCol(idx); setPvSortDir('desc'); }
          };
          const sortArrow = (idx) => pvSortCol === idx ? (pvSortDir === 'asc' ? ' ▲' : ' ▼') : '';

          // Style per level — gradasi bertingkat (tema biru)
          const LV = {
            0: { bg: 'linear-gradient(90deg,rgb(var(--brand-50)),#f8fbff)', solid: 'rgb(var(--brand-50))', weight: 800, color: '#0f172a', indent: 0,  size: 13, dot: 'rgb(var(--brand-600))' },
            1: { bg: '#f8fafc', solid: '#f8fafc', weight: 600, color: '#334155', indent: 22, size: 12, dot: '#60a5fa' },
            2: { bg: '#fff',    solid: '#fff',    weight: 400, color: '#64748b', indent: 44, size: 12, dot: '#cbd5e1' },
          };
          // gradasi badge total per level
          const totalBg = (level) => level === 0
            ? 'linear-gradient(90deg,rgb(var(--brand-600)),rgb(var(--brand-500)))' : level === 1 ? 'rgb(var(--brand-100))' : 'rgb(var(--brand-50))';
          const totalColor = (level) => level === 0 ? '#fff' : 'rgb(var(--brand-700))';
          // kelompok tahun untuk super-header
          const yearGroups = [];
          pivot.cols.forEach((c, i) => {
            const last = yearGroups[yearGroups.length - 1];
            if (last && last.year === c.year) last.span++;
            else yearGroups.push({ year: c.year, span: 1 });
          });

          // Render satu baris node (rekursif). path = { bu, dept, sec } sesuai level.
          const renderRow = (node, level, parentKey, path) => {
            const key = parentKey ? parentKey + '>' + node.label : node.label;
            const hasChildren = node.children && node.children.length > 0;
            const isOpen = pvExpanded.has(key);
            const lv = LV[level];
            // konteks path untuk drilldown
            const myPath = level === 0 ? { bu: node.label }
              : level === 1 ? { ...path, dept: node.label }
              : { ...path, sec: node.label };
            const out = [];
            out.push(
              <tr key={key} style={{ background: lv.bg }}>
                <td style={{ padding: '8px 12px', paddingLeft: 12 + lv.indent, borderBottom: '1px solid #eef2f7',
                  fontWeight: lv.weight, fontSize: lv.size, color: lv.color, whiteSpace: 'nowrap',
                  position: 'sticky', left: 0, background: lv.solid, zIndex: 1, boxShadow: '2px 0 6px rgba(15,23,42,0.06)',
                  cursor: hasChildren ? 'pointer' : 'default' }}
                  onClick={hasChildren ? () => toggle(key) : undefined}>
                  {hasChildren
                    ? <span style={{ display: 'inline-block', width: 16, color: '#94a3b8', fontSize: 11 }}>{isOpen ? '▾' : '▸'}</span>
                    : <span style={{ display: 'inline-block', width: 16 }} />}
                  <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: lv.dot, marginRight: 8, verticalAlign: 'middle' }} />
                  {node.label}
                </td>
                {node.cells.map((v, j) => (
                  <td key={j}
                    onClick={v > 0 ? () => loadDrill({
                      measure: pivotMeasure, ...myPath,
                      year: pivot.cols[j].year, month: pivot.cols[j].month,
                      title: `${[myPath.bu, myPath.dept, myPath.sec].filter(Boolean).join(' › ')} — ${colLabel(pivot.cols[j])}`,
                    }) : undefined}
                    title={v > 0 ? T.clickReason : ''}
                    style={{ padding: '8px 12px', borderBottom: '1px solid #eef2f7', textAlign: 'right',
                      fontSize: lv.size, fontWeight: level === 0 ? 700 : 400,
                      color: v > 0 ? lv.color : '#cbd5e1',
                      cursor: v > 0 ? 'pointer' : 'default' }}
                    onMouseEnter={v > 0 ? (e) => { e.currentTarget.style.background = '#fef3c7'; } : undefined}
                    onMouseLeave={v > 0 ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}>
                    {fmtCell(v)}
                  </td>
                ))}
                <td style={{ padding: '6px 10px', borderBottom: '1px solid #eef2f7', textAlign: 'right',
                  position: 'sticky', right: 0, background: lv.solid, zIndex: 1 }}>
                  <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6,
                    fontWeight: 700, fontSize: lv.size, background: totalBg(level), color: totalColor(level),
                    minWidth: 54, textAlign: 'right' }}>
                    {fmtCell(node.rowTotal)}
                  </span>
                </td>
              </tr>
            );
            if (hasChildren && isOpen) {
              for (const child of node.children) out.push(...renderRow(child, level + 1, key, myPath));
            }
            return out;
          };

          return (
            <div style={{ overflowX: 'auto', maxHeight: 600, overflowY: 'auto', borderRadius: 10 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 'max-content', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 4 }}>
                  {/* Super-header: tahun */}
                  <tr>
                    <th rowSpan={2} style={{ background: '#1e3a5f', padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#cbd5e1', whiteSpace: 'nowrap', minWidth: 250, position: 'sticky', left: 0, zIndex: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      BU › Dept › Section
                    </th>
                    {yearGroups.map((g, gi) => (
                      <th key={gi} colSpan={g.span} style={{ background: '#1e3a5f', padding: '6px 12px', textAlign: 'center', fontWeight: 700, fontSize: 12, color: '#93c5fd', borderLeft: gi > 0 ? '1px solid #2d4a6b' : 'none', borderBottom: '1px solid #2d4a6b' }}>
                        {g.year}
                      </th>
                    ))}
                    <th rowSpan={2} style={{ background: '#0f2942', padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#fff', whiteSpace: 'nowrap', position: 'sticky', right: 0, zIndex: 6, textTransform: 'uppercase', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => sortCol('total')} title={T.clickSort}>
                      {T.rowTotal}{sortArrow('total')}
                    </th>
                  </tr>
                  {/* Header bulan */}
                  <tr>
                    {pivot.cols.map((c, j) => (
                      <th key={c.key} onClick={() => sortCol(j)} title={T.clickSort}
                        style={{ background: pvSortCol === j ? 'rgb(var(--brand-600))' : '#26466b', padding: '7px 12px', textAlign: 'right', fontWeight: 600, fontSize: 10.5, color: '#e2e8f0', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {MONTHS[(c.month || 1) - 1] || c.month}{sortArrow(j)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.flatMap(bu => renderRow(bu, 0, '', {}))}
                  {/* Baris total kolom */}
                  <tr>
                    <td style={{ padding: '9px 12px', borderTop: '2px solid #cbd5e1', fontWeight: 700, fontSize: 12, color: '#fff', position: 'sticky', left: 0, background: '#1e3a5f', zIndex: 2, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{T.colTotal}</td>
                    {pivot.colTotals.map((v, j) => (
                      <td key={j} style={{ padding: '9px 12px', borderTop: '2px solid #cbd5e1', textAlign: 'right', fontWeight: 700, fontSize: 12, color: '#fff', background: '#26466b' }}>{fmtCell(v)}</td>
                    ))}
                    <td style={{ padding: '7px 10px', borderTop: '2px solid #cbd5e1', textAlign: 'right', position: 'sticky', right: 0, background: '#0f2942', zIndex: 2 }}>
                      <span style={{ display: 'inline-block', padding: '4px 11px', borderRadius: 6, fontWeight: 800, fontSize: 12, background: 'linear-gradient(90deg,rgb(var(--brand-700)),rgb(var(--brand-500)))', color: '#fff' }}>{fmtCell(pivot.grandTotal)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}
      </Card>
      )}

      {/* ── Modal: Breakdown per Reason ── */}
      {drill && (
        <div onClick={() => { setDrill(null); setDrillData(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, maxWidth: 640, width: '100%', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{T.reasonBreakdown}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{drill.title}</div>
              </div>
              <button onClick={() => { setDrill(null); setDrillData(null); }}
                style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '0.5rem 0' }}>
              {drillLoading ? <Loading /> : !drillData || drillData.data.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>{T.noData}</div>
              ) : (() => {
                const fmtV = (v) => drill.measure === 'amount' ? 'Rp ' + fmt(v)
                  : drill.measure === 'count' ? fmt(v) : parseFloat(v || 0).toFixed(1) + ' h';
                const max = Math.max(1, ...drillData.data.map(r => r.value || 0));
                return (
                  <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px 16px', fontSize: 11, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>{T.colReason}</th>
                        <th style={{ textAlign: 'right', padding: '8px 16px', fontSize: 11, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', width: 140 }}>{T.value}</th>
                        <th style={{ textAlign: 'right', padding: '8px 16px', fontSize: 11, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', width: 70 }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillData.data.map((r, i) => {
                        const pct = drillData.total > 0 ? (r.value / drillData.total) * 100 : 0;
                        return (
                          <tr key={i}>
                            <td style={{ padding: '7px 16px', borderBottom: '1px solid #f8fafc', color: '#0f172a' }}>
                              <div style={{ position: 'relative' }}>
                                <div style={{ position: 'absolute', inset: 0, width: `${(r.value / max) * 100}%`, background: 'rgba(245,158,11,0.14)', borderRadius: 4 }} />
                                <span style={{ position: 'relative' }}>{r.reason} <span style={{ color: '#94a3b8', fontSize: 11 }}>({r.sessions}×)</span></span>
                              </div>
                            </td>
                            <td style={{ padding: '7px 16px', borderBottom: '1px solid #f8fafc', textAlign: 'right', fontWeight: 600 }}>{fmtV(r.value)}</td>
                            <td style={{ padding: '7px 16px', borderBottom: '1px solid #f8fafc', textAlign: 'right', color: '#64748b' }}>{pct.toFixed(0)}%</td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td style={{ padding: '9px 16px', borderTop: '2px solid #e2e8f0', fontWeight: 700, color: '#334155' }}>{T.grandTotal}</td>
                        <td style={{ padding: '9px 16px', borderTop: '2px solid #e2e8f0', textAlign: 'right', fontWeight: 800, color: 'rgb(var(--brand-700))' }}>{fmtV(drillData.total)}</td>
                        <td style={{ padding: '9px 16px', borderTop: '2px solid #e2e8f0', textAlign: 'right', color: '#64748b' }}>100%</td>
                      </tr>
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
